'use client';

import { Container, useCartStore } from 'components/shared';
import { H2, P } from './StyleConstants';
import { CustomImage as Image } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { Tabs } from 'components/shared/Tabs';
import { useState } from 'react';
import { UHHomeFill, UHRingFill } from 'assets/icons';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import styles from './FeatureBentoSection.module.css';

type CardElementProps = React.HTMLAttributes<HTMLDivElement> & {
  $isVisible?: boolean;
  $animationDelay?: number;
  $fullWidth?: boolean;
  $lgFlexRow?: boolean;
  $color?: string;
};

const CardElement = ({
  $isVisible,
  $animationDelay,
  $fullWidth,
  $lgFlexRow,
  $color,
  className,
  style,
  children,
  ...props
}: CardElementProps) => {
  return (
    <div
      className={`${styles.cardElement}${className ? ` ${className}` : ''}`}
      data-visible={$isVisible ? 'true' : 'false'}
      data-lg-flex-row={$lgFlexRow ? 'true' : undefined}
      style={
        {
          ...style,
          '--anim-delay': `${$animationDelay || 0}ms`,
          ...($color ? { '--card-color': $color } : {}),
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </div>
  );
};

export enum FeatureBentoTab {
  RING = 'ring',
  HOME = 'home',
}

export const FeatureBentoSection = ({
  className,
  defaultAvtiveTab,
}: {
  className?: string;
  defaultAvtiveTab?: FeatureBentoTab;
}) => {
    // Define an enum for the tabs
    const store = useCartStore((s) => s.store);

    const ringName = store === ShopifyStore.US ? 'Ring PRO' : 'Ring AIR';

    // US (Ring PRO) uses its own card imagery; other regions keep the CDN shots.
    const weightLossBg =
      store === ShopifyStore.US
        ? getCompressedAssetUrl('/web_v2/uhx/weightloss.png/w_1080')
        : getCompressedAssetUrl(
            '/web_v2/ultrahumanX/landing-page/ring-w-jumping-rope.png',
          );
    const accidentalDamageBg =
      store === ShopifyStore.US
        ? getCompressedAssetUrl('/web_v2/uhx/ring-pro-accidental-damage.webp')
        : getCompressedAssetUrl(
            '/web_v2/ultrahumanX/landing-page/ring-in-hand.png',
          );

    const [activeTab, setActiveTab] = useState<FeatureBentoTab>(
      defaultAvtiveTab ?? FeatureBentoTab.RING,
    );

    const M1UhxCard = {
      id: 'm1-discount',
      fullWidth: true,
      component: (isVisible: boolean, delay: number) => (
        <CardElement
          $isVisible={isVisible}
          $animationDelay={delay}
          $fullWidth={true}
          $lgFlexRow={true}
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Image
            src="web_v2/ultrahumanX/landing-page/m1-uhx.png"
            alt="hello"
            height="500"
            width="500"
            style={{
              alignSelf: 'center',
            }}
          />
          <div className="">
            <h4
              className={styles.lg}
              style={{
                maxWidth: '14ch',
                // mixBlendMode: 'plus-lighter',
                lineHeight: '1.2',
              }}
            >
              Enjoy 15% off on all M1 plans
            </h4>
            {/* <p className="description secondary">
              Hit your weight loss goals with Ultrahuman Ring AIR? We make it
              easy to size down with a free ring replacement, available once
              during your coverage period.
            </p> */}
          </div>
        </CardElement>
      ),
    };

    const ringCards: Array<Record<string, any>> = [
      {
        id: 'ring-theft',
        component: (isVisible: boolean, delay: number) => (
          <CardElement $isVisible={isVisible} $animationDelay={delay}>
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 className={`${styles.lg} ${styles['bg-grad-blue']}`} style={{ marginTop: '8px' }}>
              Theft
              <br />
              and loss
              <br />
              coverage.
            </h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              Your peace of mind is precious. We&apos;ll cover theft or loss
              redeemable once during your coverage period.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'ring-weight',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundImage: `url(${weightLossBg})`,
            }}
          >
            <div className={styles.overlay}></div>
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 style={{ marginTop: '8px' }}>Weight loss coverage</h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              Hit your weight loss goals with Ultrahuman {ringName}? We make it
              easy to size down with a free ring replacement, available once
              during your coverage period.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'ring-pregnancy',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            $color="#fff"
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/landing-page/ring-pregnancy-2.png',
              )})`,
            }}
          >
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 style={{ marginTop: '8px' }}>Pregnancy Fit Coverage</h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              Your body changes during pregnancy and your ring fit can too. We
              offer a one-time size change per coverage period so your health
              tracking adapts with you, not the other way around.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'ring-damage',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundImage: `url(${accidentalDamageBg})`,
            }}
          >
            <h4 style={{ maxWidth: '16ch' }}>
              Accidental damage and knock protection
            </h4>
          </CardElement>
        ),
      },
      {
        id: 'ring-support',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            $color="#000000"
            style={{
              backgroundColor: '#eeeeee',
              color: '#000000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <h4>Priority support</h4>
            <Image
              className={styles['w-full']}
              src="web_v2/ultrahumanX/landing-page/priority-support.png/w_1180"
              width="1256"
              height="1126"
              alt="ultracare"
            />
          </CardElement>
        ),
      },
      {
        id: 'ring-returns',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/bg/GREEN-BLUE-GRADIENT.png',
              )})`,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <h4
              className={styles.xl}
              style={{
                maxWidth: 'unset',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.28)',
                mixBlendMode: 'plus-lighter',
              }}
            >
              Free,
              <br />
              express
              <br />
              and hassle-free
              <br />
              returns and replacement.
            </h4>
          </CardElement>
        ),
      },
      { ...M1UhxCard },
    ];

    const homeCards = [
      // {
      //   id: 'home-theft',
      //   component: (isVisible: boolean, delay: number) => (
      //     <CardElement $isVisible={isVisible} $animationDelay={delay}>
      //       <p className={styles.caption}>Only with 2 year coverage plan</p>
      //       <h4 className={`${styles.lg} ${styles['bg-grad-blue']}`} style={{ marginTop: '8px' }}>
      //         Theft
      //         <br />
      //         and loss
      //         <br />
      //         coverage.
      //       </h4>
      //       <p className={styles.description} style={{ maxWidth: '32ch' }}>
      //         Your peace of mind is precious. We&apos;ll cover theft or loss
      //         redeemable once during your coverage period.
      //       </p>
      //     </CardElement>
      //   ),
      // },
      {
        id: 'home-env-protection',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/landing-page/home/env-home-bg.png',
              )})`,
              backgroundPositionY: 'bottom',
            }}
          >
            {/* <div className={styles.overlay}></div> */}
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 style={{ marginTop: '8px' }}>Environmental wear protection</h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              Covers damage from humidity, dust, power fluctuations, and natural
              events. Includes one free repair or replacement per coverage
              period after diagnostic.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'home-beta-access',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/bg/green-lines-grad-bg.png',
              )})`,
              backgroundPosition: 'right',
            }}
          >
            {/* <div className={styles.overlay}></div> */}
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 style={{ marginTop: '8px', color: '#00FF7A' }}>
              Ultrahuman Beta Access
            </h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              As an UltrahumanX member, you’ll gain exclusive early access to
              experimental features designed for Ultrahuman Home.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'home-accidental-damage',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            style={{
              backgroundColor: 'rgb(18,18,18)',
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/landing-page/home/home-view.png',
              )})`,
              backgroundPositionY: '24px',
            }}
          >
            {/* <div className={styles.overlay}></div> */}
            <p className={styles.caption}>Only with 2 year coverage plan</p>
            <h4 style={{ marginTop: '8px' }}>Accidental Damage Protection</h4>
            <p className={styles.description} style={{ maxWidth: '32ch' }}>
              Covers one repair or replacement if the device is accidentally
              damaged during setup, repositioning, or handling - built for the
              everyday realities of home use.
            </p>
          </CardElement>
        ),
      },
      {
        id: 'home-support',
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            $color="#000000"
            style={{
              backgroundColor: '#eeeeee',
              color: '#000000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <h4>Priority support</h4>
            <Image
              className={styles['w-full']}
              src="web_v2/ultrahumanX/landing-page/home/home-priority-support.png"
              width="1256"
              height="1126"
              alt="ultracare"
            />
          </CardElement>
        ),
      },
      {
        id: 'home-returns',
        fullWidth: true,
        component: (isVisible: boolean, delay: number) => (
          <CardElement
            $isVisible={isVisible}
            $animationDelay={delay}
            $fullWidth={true}
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/ultrahumanX/landing-page/home/green-gradient.png',
              )})`,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <h4
              className={styles.xl}
              style={{
                maxWidth: '14ch',
                fontWeight: 600,
                color: 'rgb(0, 255, 122, 1)',
                // mixBlendMode: 'plus-lighter',
                lineHeight: '1.2',
              }}
            >
              Free, express and hassle-free returns and replacement.
            </h4>
          </CardElement>
        ),
      },
      { ...M1UhxCard },
    ];

    const tabsData = [
      {
        title: ringName,
        value: FeatureBentoTab.RING,
        icon: <UHRingFill fill={'#000000'} />,
      },
      {
        title: 'Home',
        value: FeatureBentoTab.HOME,
        icon: <UHHomeFill fill={'#000000'} />,
      },
    ];

    // const currentCards =
    //   activeTab === FeatureBentoTab.RING ? ringCards : homeCards;

    return (
      <section className={`${styles.root}${className ? ` ${className}` : ''}`}>
        <Container>
          <div className={styles['uhx-text-content']}>
            <H2 className="title">Unbeatable membership benefits</H2>
            <P>
              Designed to help you unlock the best price and protect all your
              Ultrahuman products.
            </P>
            <div className="tabs-container">
              <Tabs
                tabs={tabsData}
                onChange={(i) => setActiveTab(i as FeatureBentoTab)}
                value={activeTab}
                compactSm={false}
              />
            </div>
          </div>
          <div className={styles['bento-content']}>
            {/* Render all cards but control visibility with animations */}
            {ringCards.map((card, index) => (
              <div
                key={card.id}
                className={card.fullWidth ? styles['full-width'] : ''}
                style={{
                  display: activeTab === FeatureBentoTab.RING ? 'block' : 'none',
                }}
              >
                {card.component(activeTab === FeatureBentoTab.RING, index * 150)}
              </div>
            ))}

            {homeCards.map((card, index) => (
              <div
                key={card.id}
                className={card.fullWidth ? styles['full-width'] : ''}
                style={{
                  display:
                    activeTab === FeatureBentoTab.HOME ? 'block' : 'none',
                }}
              >
                {card.component(
                  activeTab === FeatureBentoTab.HOME,
                  index * 150,
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>
    );
};
